import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';

// ── Types ──

interface AdminOrg {
  id: string;
  name: string;
}

// Mirrors GET /api/v1/usage/admin/org/{org_id}/interactions. The previous
// shape here (timestamp / tokens / credits / org_name) matched no field the
// endpoint returns, so every row crashed on `credits.toFixed` and took the
// whole page down. Fields stay optional-tolerant because a log row written by
// an older service build can legitimately omit the newer ones.
interface Interaction {
  id: string;
  created_at: string | null;
  provider: string;
  model: string;
  feature: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cached_input_tokens?: number;
  credits_consumed: number;
  used_byok: boolean;
  status: string;
  error_message: string | null;
  latency_ms: number | null;
  prompt_text: string | null;
  response_text: string | null;
}

interface FeatureUsage {
  feature: string;
  credits: number;
  requests: number;
}

interface ModelUsage {
  provider: string;
  model: string;
  credits: number;
  requests: number;
}

interface OrgUsageBreakdown {
  by_feature: FeatureUsage[];
  by_model: ModelUsage[];
}

interface InteractionsResponse {
  items: Interaction[];
  total: number;
  page: number;
  pages: number;
}

// ── Helpers ──

function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

function getStatusColor(status: string | null | undefined): string {
  switch ((status || '').toLowerCase()) {
    case 'success':
      return 'bg-green-50 text-green-700';
    case 'error':
    case 'failed':
      return 'bg-red-50 text-red-700';
    case 'timeout':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-slate-50 text-slate-700';
  }
}

// ── Component ──

export function InteractionHistoryPage() {
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const pageSize = 50;

  // Fetch all orgs for the selector
  const orgsQuery = useQuery<AdminOrg[]>({
    queryKey: ['admin', 'organizations'],
    queryFn: () => apiClient.get('/api/v1/admin/organizations').then((r) => r.data),
  });

  // Which feature is actually burning the org's credits. The endpoint has
  // always returned by_feature/by_model; no admin page rendered it, so there
  // was no way to tell copilot usage from SDK chat usage.
  const breakdownQuery = useQuery<OrgUsageBreakdown>({
    queryKey: ['usage-breakdown', selectedOrgId, days],
    queryFn: () =>
      apiClient
        .get(`/api/v1/usage/admin/org/${selectedOrgId}?days=${days}`)
        .then((r) => r.data),
    enabled: !!selectedOrgId,
  });

  // Fetch interactions for selected org
  const interactionsQuery = useQuery<InteractionsResponse>({
    queryKey: ['usage-interactions', selectedOrgId, page],
    queryFn: () =>
      apiClient
        .get(`/api/v1/usage/admin/org/${selectedOrgId}/interactions?page=${page}&page_size=${pageSize}`)
        .then((r) => r.data),
    enabled: !!selectedOrgId,
  });

  const interactions = interactionsQuery.data;
  const orgs = orgsQuery.data || [];
  const byFeature = breakdownQuery.data?.by_feature ?? [];
  const byModel = breakdownQuery.data?.by_model ?? [];
  const totalFeatureCredits = byFeature.reduce((sum, f) => sum + f.credits, 0);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Interaction History</h1>
        <p className="text-slate-600 mt-2">
          Browse AI interaction logs per organization
        </p>
      </div>

      {/* Org Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Select Organization
            </label>
            <select
              value={selectedOrgId}
              onChange={(e) => {
                setSelectedOrgId(e.target.value);
                setPage(1);
                setExpandedRow(null);
              }}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="">-- Choose an organization --</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Range
            </label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-4 py-2.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
          {interactions && (
            <div className="text-sm text-slate-500 pb-2.5">
              {interactions.total} interaction{interactions.total !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      </div>

      {/* Credit consumption by feature — answers "is it the extension or chat?" */}
      {selectedOrgId && byFeature.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-1">
              Credits by feature
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              {totalFeatureCredits.toLocaleString()} credits over the last {days} days
            </p>
            <div className="space-y-3">
              {byFeature.map((f) => {
                const pct = totalFeatureCredits > 0 ? (f.credits / totalFeatureCredits) * 100 : 0;
                return (
                  <div key={f.feature}>
                    <div className="flex items-baseline justify-between text-sm mb-1">
                      <span className="font-medium text-slate-800">{f.feature || 'unknown'}</span>
                      <span className="text-slate-500 tabular-nums">
                        {formatNumber(f.credits)} cr · {pct.toFixed(1)}% · {formatNumber(f.requests)} calls
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${Math.max(pct, 1)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-1">
              Credits by model
            </h2>
            <p className="text-xs text-slate-500 mb-4">Which provider the spend lands on</p>
            <div className="space-y-2">
              {byModel.map((m) => (
                <div
                  key={`${m.provider}-${m.model}`}
                  className="flex items-baseline justify-between text-sm py-1.5 border-b border-slate-50 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-slate-800 truncate">{m.model}</div>
                    <div className="text-xs text-slate-400">{m.provider}</div>
                  </div>
                  <span className="text-slate-500 tabular-nums whitespace-nowrap pl-4">
                    {formatNumber(m.credits)} cr · {formatNumber(m.requests)} calls
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading / Empty States */}
      {!selectedOrgId && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="text-slate-400 text-sm">
            Select an organization above to view interaction history
          </div>
        </div>
      )}

      {selectedOrgId && interactionsQuery.isLoading && (
        <div className="text-center py-12 text-slate-400">Loading interactions...</div>
      )}

      {selectedOrgId && interactionsQuery.isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-700 text-sm">
          Failed to load interactions. Please try again.
        </div>
      )}

      {/* Interactions Table */}
      {interactions && interactions.items.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3 w-8"></th>
                  <th className="px-6 py-3">Timestamp</th>
                  <th className="px-6 py-3">Feature</th>
                  <th className="px-6 py-3">Model</th>
                  <th className="px-6 py-3 text-right">Tokens</th>
                  <th className="px-6 py-3 text-right">Credits</th>
                  <th className="px-6 py-3 text-center">Status</th>
                  <th className="px-6 py-3 text-right">Latency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {interactions.items.map((item) => (
                  <InteractionRow
                    key={item.id}
                    item={item}
                    isExpanded={expandedRow === item.id}
                    onToggle={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {interactions && interactions.items.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="text-slate-400 text-sm">No interactions found for this organization</div>
        </div>
      )}

      {/* Pagination */}
      {interactions && interactions.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            Page {interactions.page} of {interactions.pages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(interactions.pages, p + 1))}
              disabled={page >= interactions.pages}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Row Component ──

function InteractionRow({
  item,
  isExpanded,
  onToggle,
}: {
  item: Interaction;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={onToggle}>
        <td className="px-6 py-3">
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </td>
        <td className="px-6 py-3 text-slate-700 whitespace-nowrap">
          {formatTimestamp(item.created_at)}
        </td>
        <td className="px-6 py-3">
          <span className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-semibold">
            {item.feature || 'unknown'}
          </span>
        </td>
        <td className="px-6 py-3 text-slate-700 font-mono text-xs">{item.model}</td>
        <td className="px-6 py-3 text-right text-slate-700">
          {formatNumber(item.total_tokens)}
          {!!item.cached_input_tokens && (
            <span className="block text-xs text-emerald-600">
              {formatNumber(item.cached_input_tokens)} cached
            </span>
          )}
        </td>
        <td className="px-6 py-3 text-right font-semibold text-slate-900">
          {formatNumber(item.credits_consumed)}
        </td>
        <td className="px-6 py-3 text-center">
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(item.status)}`}>
            {item.status || 'unknown'}
          </span>
        </td>
        <td className="px-6 py-3 text-right text-slate-700">
          {item.latency_ms === null || item.latency_ms === undefined ? '—' : `${item.latency_ms}ms`}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={8} className="px-6 py-4 bg-slate-50 border-t border-slate-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Prompt
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-800 font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {item.prompt_text || <span className="text-slate-400 italic">No prompt text available</span>}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Response
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-800 font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {item.response_text || <span className="text-slate-400 italic">No response text available</span>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
